import { ArrowSquareOutIcon, ArrowsOutSimpleIcon, BroadcastIcon, CheckCircleIcon, ClockIcon, GitMergeIcon, InfoIcon, UserIcon, XCircleIcon, XIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { Fragment, type KeyboardEvent, type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { q } from "../api.ts";
import { type ActionFilter, type ActionGroup, actionProgress, campaignOfAction, filterActions, groupActions, groupStatus, isCollapsed, nextTime, planActionOf, readCollapsed } from "../action-items.ts";
import { channelName, ChannelChip, ChannelName } from "../ui/channel.tsx";
import { CampaignChip } from "../ui/campaign-chip.tsx";
import { dateTime } from "../format.ts";
import { ACTIONS_LABELS as L, activityText, AREA_LABELS as A, INBOX_LABELS as I, KIT_LABELS, readingLabel, titleText } from "../labels.ts";
import { ExperimentResultEntry, MeasurementPlanSection } from "../measurement.tsx";
import { ActionMetricMeasurement } from "../metrics.tsx";
import { readPref, writePref } from "../prefs.ts";
import { Button, DetailPanel, ErrorText, Page, Quiet, Reasons, Section, TopBar } from "../ui.tsx";
import { GroupHeader, ListRow } from "../ui/list.tsx";
import { PropertyList, PropertyRow } from "../ui/props.tsx";
import { Activity, type ActivityItem, AgentText } from "../ui/review.tsx";
import { Select } from "../ui/select.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { Chip, ResultBadge } from "../ui/status.tsx";
import { actionStatus } from "../week-items.ts";
import { ActionExecutionControls, ActionExecutionSummary, ActionExecutor, ActionStatusChip } from "../action-execution.tsx";

// A product's actions (docs/design-system.md §0.4e, plan M7 step 7h), after
// Linear's issues: grouped by lifecycle, filtered by campaign, channel and
// executor, with the detail on the right and as a full page. Governance is
// not done here: approvals and readings are decided in the inbox, and the
// detail's main button opens that review.

const GROUPS_KEY = "actions-groups";
const EXECUTORS: readonly C.ActionExecutor[] = ["agent", "person", "zooi"];

export interface ActionsSearch { selected?: string; campaign?: string; channel?: string; executor?: C.ActionExecutor }

export function actionsSearch(search: Record<string, unknown>): ActionsSearch {
  const text = (value: unknown) => (typeof value === "string" && value.length > 0 && value.length <= 80 ? value : undefined);
  const executor = EXECUTORS.find((value) => value === search.executor);
  return { selected: text(search.selected), campaign: text(search.campaign), channel: text(search.channel), executor };
}


export function ActionsPage() {
  const { productId } = useParams({ strict: false }) as { productId: string };
  const search = useSearch({ strict: false }) as ActionsSearch;
  const navigate = useNavigate();
  const products = useQuery(q.products);
  const product = products.data?.find((candidate) => candidate.id === productId);
  const actions = useQuery(q.actions(productId));
  const campaigns = useQuery(q.productCampaigns(productId));
  const campaignOf = useMemo(() => campaignOfAction(campaigns.data ?? []), [campaigns.data]);
  const filter: ActionFilter = { campaign: search.campaign ?? null, channel: search.channel ?? null, executor: search.executor ?? null };
  const all = actions.data ?? [];
  const shown = filterActions(all, filter, campaignOf);
  const groups = groupActions(shown);
  const [folded, setFolded] = useState(() => readCollapsed(readPref(GROUPS_KEY)));
  const visible = groups.flatMap((entry) => (isCollapsed(folded, entry.group) ? [] : entry.actions));
  const [cursor, setCursor] = useState<string | null>(search.selected ?? null);
  const rows = useRef(new Map<string, HTMLLIElement>());
  const current = visible.find((action) => action.id === (search.selected ?? cursor)) ?? visible[0];
  const zone = product?.settings.timezone ?? "UTC";
  const channels = [...new Set(all.map((action) => action.channel))].sort();

  const setSearch = useCallback((next: Partial<ActionsSearch>, replace = false) => {
    void navigate({ to: "/p/$productId/actions", params: { productId }, search: (prev: ActionsSearch) => ({ ...prev, ...next }), replace });
  }, [navigate, productId]);
  const open = (id: string) => { setCursor(id); setSearch({ selected: id }); };
  const close = () => { setSearch({ selected: undefined }); requestAnimationFrame(() => { if (current) rows.current.get(current.id)?.focus(); }); };
  const toggle = (group: ActionGroup) => setFolded((state) => {
    const next = { ...state, [group]: !isCollapsed(state, group) };
    writePref(GROUPS_KEY, JSON.stringify(next));
    return next;
  });
  const move = (delta: number) => {
    if (!visible.length) return;
    const index = Math.max(0, visible.findIndex((action) => action.id === current?.id));
    const next = visible[Math.min(visible.length - 1, Math.max(0, index + delta))];
    setCursor(next.id);
    rows.current.get(next.id)?.focus();
    // With the detail open, it follows the selection, as in Linear.
    if (search.selected && next.id !== search.selected) setSearch({ selected: next.id }, true);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    if (key === "j" || key === "ArrowDown") move(1);
    else if (key === "k" || key === "ArrowUp") move(-1);
    else if (key === "Home") move(-visible.length);
    else if (key === "End") move(visible.length);
    else if (key === "Enter" && current) open(current.id);
    else if (key === "Escape" && search.selected) close();
    else return;
    event.preventDefault();
  };

  const crumbs = [{ label: product?.name ?? A.product, to: "/p/$productId/week", params: { productId } }, { label: L.title }];
  if (actions.isLoading) return <div className="z-inbox-page"><TopBar crumbs={crumbs} /><div className="z-inbox"><div className="z-inbox-list"><Skeleton /></div></div></div>;
  const filtered = Boolean(filter.campaign || filter.channel || filter.executor);
  return <div className="z-inbox-page">
    <TopBar crumbs={crumbs} actions={visible.length > 0 && <span className="z-inbox-keys">{L.keys}</span>} />
    <div className={`z-inbox${search.selected ? " has-detail" : ""}`}>
      <div className="z-inbox-list">
        <header className="z-inbox-head">
          <h1 id="actions-title">{L.title}</h1>
          {all.length > 0 && <span className="z-count">{shown.length}</span>}
        </header>
        {all.length > 0 && <div className="z-filters" role="group" aria-label={L.filters}>
          <Select label={L.campaign} value={filter.campaign ?? ""} onValueChange={(value) => setSearch({ campaign: value || undefined }, true)}
            options={[{ value: "", label: L.allCampaigns }, ...(campaigns.data ?? []).map((campaign) => ({ value: campaign.id, label: campaign.title }))]} />
          <Select label={L.channel} value={filter.channel ?? ""} onValueChange={(value) => setSearch({ channel: value || undefined }, true)}
            options={[{ value: "", label: L.allChannels }, ...channels.map((channel) => ({ value: channel, label: channelName(channel) }))]} />
          <Select label={L.executor} value={filter.executor ?? ""} onValueChange={(value) => setSearch({ executor: EXECUTORS.find((executor) => executor === value) }, true)}
            options={[{ value: "", label: L.anyExecutor }, ...EXECUTORS.map((value) => ({ value, label: L.who[value] }))]} />
          {filtered && <Button size="sm" tone="ghost" icon={<XIcon />} onClick={() => setSearch({ campaign: undefined, channel: undefined, executor: undefined }, true)}>{L.clearFilters}</Button>}
        </div>}
        <ErrorText error={actions.error ?? campaigns.error} />
        {actions.data && !all.length && <Quiet title={L.emptyTitle}><p>{L.emptyNote}</p></Quiet>}
        {all.length > 0 && !shown.length && <p className="z-empty">{L.emptyFiltered}</p>}
        {groups.length > 0 && <ul className="z-rows z-boxed" role="listbox" aria-labelledby="actions-title" onKeyDown={onKeyDown}>
          {groups.map(({ group, actions: members }) => {
            const collapsed = isCollapsed(folded, group);
            return <Fragment key={group}>
              <li role="presentation"><GroupHeader label={L.group[group]} count={members.length} status={groupStatus(group)} collapsed={collapsed} onToggle={() => toggle(group)} /></li>
              {!collapsed && members.map((action) => <ListRow key={action.id} id={`action-${action.id}`} selected={action.id === current?.id}
                status={actionStatus(action)} title={titleText(action.title)}
                chips={<>{campaignOf.get(action.id) && <CampaignChip>{campaignOf.get(action.id)!.title}</CampaignChip>}<ChannelChip channel={action.channel} /></>}
                executor={<ActionExecutor action={action} />}
                meta={<NextTime action={action} zone={zone} />}
                onSelect={() => setCursor(action.id)} onOpen={() => open(action.id)}
                rowRef={(node) => { if (node) rows.current.set(action.id, node); else rows.current.delete(action.id); }} />)}
            </Fragment>;
          })}
        </ul>}
      </div>
      {search.selected && <DetailPanel label={L.detail} onClose={close}>
        <ActionPanel productId={productId} actionId={search.selected} zone={zone} campaign={campaignOf.get(search.selected) ?? null} onClose={close} />
      </DetailPanel>}
    </div>
  </div>;
}

function NextTime({ action, zone }: { action: C.Action; zone: string }) {
  const next = nextTime(action);
  if (!next) return null;
  const at = dateTime(next.at, zone);
  return <>{next.kind === "matures" ? L.matures(at) : L.expires(at)}</>;
}

/** What the panel and the full page both read: the action, its history and the plan it came from. */
function useActionData(actionId: string) {
  const action = useQuery(q.action(actionId));
  const activity = useQuery(q.actionActivity(actionId));
  const planId = activity.data?.entries.find((entry) => entry.code === "proposed")?.link?.id ?? null;
  const plan = useQuery({ ...q.agentPlan(planId ?? ""), enabled: Boolean(planId) });
  return { action, activity, plan: plan.data, planId };
}

function ActionPanel({ productId, actionId, zone, campaign, onClose }: { productId: string; actionId: string; zone: string; campaign: { id: string; title: string } | null; onClose: () => void }) {
  const data = useActionData(actionId);
  const a = data.action.data;
  return <>
    <div className="z-detail-head">
      <h2>{a ? titleText(a.title) : ""}</h2>
      <Link to="/p/$productId/actions/$actionId" params={{ productId, actionId }} className="z-iconbtn" aria-label={KIT_LABELS.openFullPage} title={KIT_LABELS.openFullPage}><ArrowsOutSimpleIcon size={16} /></Link>
      <button type="button" className="z-iconbtn" aria-label={KIT_LABELS.close} title={`${KIT_LABELS.close} (Esc)`} onClick={onClose}><XIcon size={16} /></button>
    </div>
    {data.action.isLoading && <Skeleton shape="panel" />}
    <ErrorText error={data.action.error ?? data.activity.error} />
    {a && <ActionBody productId={productId} action={a} activity={data.activity.data} plan={data.plan} planId={data.planId} zone={zone} campaign={campaign} />}
  </>;
}

/** The full page of an action: the same detail, with its measurement, in a main column and a properties column. */
export function ActionPage() {
  const { productId, actionId } = useParams({ strict: false }) as { productId: string; actionId: string };
  const products = useQuery(q.products);
  const product = products.data?.find((candidate) => candidate.id === productId);
  const campaigns = useQuery(q.productCampaigns(productId));
  const campaign = campaignOfAction(campaigns.data ?? []).get(actionId) ?? null;
  const data = useActionData(actionId);
  const a = data.action.data;
  const zone = product?.settings.timezone ?? "UTC";
  const crumbs = [
    { label: product?.name ?? A.product, to: "/p/$productId/week", params: { productId } },
    { label: L.title, to: "/p/$productId/actions", params: { productId } },
    { label: a ? titleText(a.title) : L.detail },
  ];
  if (data.action.isLoading) return <Page wide crumbs={crumbs}><Skeleton shape="object" /></Page>;
  if (!a) return <Page crumbs={crumbs}><ErrorText error={data.action.error} /></Page>;
  const mode = a.measurement_plan.mode;
  const measured = a.status === "live" || a.status === "reading_due" || a.status === "evaluated";
  return <Page wide crumbs={crumbs} title={titleText(a.title)} actions={<ActionExecutionControls productId={productId} action={a} />}
    meta={<><ActionStatusChip action={a} />{a.reading && <ResultBadge result={a.reading.value === "no_signal" ? "none" : a.reading.value} operational={mode === "operational"} />}<ChannelName channel={a.channel} /><ActionExecutionSummary action={a} zone={zone} /></>}>
    <div className="z-layout-main">
      <div className="z-flow">
        <ActionBody productId={productId} action={a} activity={data.activity.data} plan={data.plan} planId={data.planId} zone={zone} campaign={campaign} page />
        <MeasurementPlanSection action={a} productId={productId} />
        {/* A business metric is bound to observational plans only (docs/design-system.md §5.11). */}
        {mode === "observational" && <ActionMetricMeasurement action={a} productId={productId} setup={!measured} />}
        {measured && <ExperimentResultEntry action={a} productId={productId} timeZone={zone} />}
      </div>
      <aside className="z-detail-summary" aria-label={KIT_LABELS.properties}>
        <h2>{KIT_LABELS.properties}</h2>
        <ActionProperties productId={productId} action={a} activity={data.activity.data} plan={data.plan} zone={zone} campaign={campaign} />
      </aside>
    </div>
    <ErrorText error={data.activity.error} />
  </Page>;
}

interface BodyProps {
  productId: string; action: C.ActionDetail; activity: C.ActionActivity | undefined; plan: C.AgentPlan | undefined; planId?: string | null;
  zone: string; campaign: { id: string; title: string } | null; page?: boolean;
}

function ActionBody({ productId, action: a, activity, plan, zone, campaign, page }: BodyProps) {
  const mode = a.measurement_plan.mode;
  const engine = plan ? I.engine[plan.engine.name] ?? plan.engine.name : I.engine.other;
  const why = planActionOf(plan, a.id)?.action.why;
  return <>
    {!page && <><div className="z-detail-meta z-action-panel-tools"><ActionStatusChip action={a} />{a.reading && <ResultBadge result={a.reading.value === "no_signal" ? "none" : a.reading.value} operational={mode === "operational"} />}<ActionExecutionControls productId={productId} action={a} compact /></div><ActionExecutionSummary action={a} zone={zone} /></>}
    {a.status === "reading_due" && <div className="z-flow">
      <p className="z-small z-muted">{L.governanceNote}</p>
      <p><Link to="/inbox/readings/$readingId" params={{ readingId: a.id }} className="z-btn primary">{L.confirmReading}</Link></p>
    </div>}
    {!page && <Section title={KIT_LABELS.properties}><ActionProperties productId={productId} action={a} activity={activity} plan={plan} zone={zone} campaign={campaign} /></Section>}
    {(why || a.why_now.length > 0) && <Section title={L.why}>
      {why ? <AgentText name={engine} engine={plan?.engine.name} label={engine} lines={4}>{why}</AgentText> : <Reasons reasons={a.why_now} />}
    </Section>}
    {a.target && <Section title={I.replyTo}>
      <p><a className="z-link" href={a.target.url} target="_blank" rel="noreferrer noopener">{a.target.title} <ArrowSquareOutIcon size={12} /></a></p>
    </Section>}
    <Section title={L.delivery}>
      {a.receipt ? <p className="z-small">
        <a className="z-link" href={a.receipt.url} target="_blank" rel="noreferrer noopener" style={{ overflowWrap: "anywhere" }}>{a.receipt.url}</a>
        {" · "}{I.verification[a.receipt.verification] ?? a.receipt.verification}
      </p> : <p className="z-empty">{L.noDelivery}</p>}
      <ActionProgress action={a} zone={zone} />
    </Section>
    {a.reading && <Section title={L.reading}>
      <p className="z-inline"><ResultBadge result={a.reading.value === "no_signal" ? "none" : a.reading.value} operational={mode === "operational"} /><span className="z-small z-muted">{readingLabel(a.reading.value, mode)} · {dateTime(a.reading.confirmed_at, zone)}</span></p>
      {a.reading.note && <p>{a.reading.note}</p>}
    </Section>}
    <Section title={L.activity}>
      {activity?.entries.length ? <Activity items={activity.entries.map((entry) => activityItem(entry, { productId, engine, mode, zone }))} /> : <p className="z-empty">{activity ? L.activityEmpty : ""}</p>}
    </Section>
  </>;
}

function ActionProgress({ action, zone }: { action: C.ActionDetail; zone: string }) {
  const { completed, next } = actionProgress(action, Date.now());
  if (!completed.length) return null;
  const operational = action.measurement_plan.mode === "operational";
  const nextLabel = next?.key === "live" ? L.nextLive
    : next?.key === "observed" ? action.matures_at ? L.observingUntil(dateTime(action.matures_at, zone)) : L.awaitObservation
    : next?.key === "reading" ? operational ? L.nextAcceptance : L.nextReading : null;
  return <ol className="z-flowline" aria-label={L.progress}>
    {completed.map((stage) => <li key={stage.key}>
      {stage.key === "reading" && operational ? L.acceptanceComplete : L.milestone[stage.key]}
      {stage.at && <> · <time dateTime={stage.at}>{dateTime(stage.at, zone)}</time></>}
    </li>)}
    {nextLabel && <li className="z-muted" aria-current="step">{nextLabel}</li>}
  </ol>;
}

function ActionProperties({ productId, action: a, activity, plan, zone, campaign }: Omit<BodyProps, "page" | "planId">) {
  const from = planActionOf(plan, a.id);
  const approved = activity?.entries.find((entry) => entry.code === "approved");
  const mode = a.measurement_plan.mode;
  const windowDays = Math.round(a.measurement_plan.window.matures_after_hours / 24);
  return <PropertyList>
    <PropertyRow label={L.status}><ActionStatusChip action={a} /></PropertyRow>
    <PropertyRow label={L.campaign}>{campaign && <Link className="z-link" to="/p/$productId/campaigns/$campaignId" params={{ productId, campaignId: campaign.id }}>{campaign.title}</Link>}</PropertyRow>
    <PropertyRow label={L.channel}><ChannelName channel={a.channel} /></PropertyRow>
    <PropertyRow label={L.tier}>{from && (I.authorship[from.action.authorship] ?? from.action.authorship)}</PropertyRow>
    <PropertyRow label={L.grant}>{approved && <Chip>{approved.actor === "zooi" ? L.grantUsed : L.grantNotUsed}</Chip>}</PropertyRow>
    <PropertyRow label={L.executor}><ActionExecutor action={a} /></PropertyRow>
    <PropertyRow label={L.measurement}>{I.mode[mode] ?? mode}</PropertyRow>
    {mode !== "operational" && <PropertyRow label={L.window}>{a.activated_at && a.matures_at ? L.windowRange(dateTime(a.activated_at, zone), dateTime(a.matures_at, zone)) : windowDays > 0 ? L.windowDays(windowDays) : null}</PropertyRow>}
    <PropertyRow label={L.playbook}>{from?.action.playbook_id && <Link className="z-link z-mono" to="/playbooks/$playbookId" params={{ playbookId: from.action.playbook_id }} search={{ product: productId }}>{from.action.playbook_id}</Link>}</PropertyRow>
  </PropertyList>;
}

const ZOOI_ICON: Partial<Record<C.ActivityCode, ReactNode>> = {
  verified: <CheckCircleIcon size={12} weight="bold" />, unverifiable: <InfoIcon size={12} weight="bold" />, verification_failed: <XCircleIcon size={12} weight="bold" />,
  pr_merged: <GitMergeIcon size={12} weight="bold" />, pr_closed: <XCircleIcon size={12} weight="bold" />, pr_landing_failed: <XCircleIcon size={12} weight="bold" />,
  live: <BroadcastIcon size={12} weight="bold" />, reading_due: <ClockIcon size={12} weight="bold" />,
};

function activityItem(entry: C.ActionActivity["entries"][number], ctx: { productId: string; engine: string; mode: string; zone: string }): ActivityItem {
  const text = activityText(entry, ctx);
  const link = entry.link?.kind === "plan" && entry.link.id
    ? <> · <Link className="z-link" to="/p/$productId/plans/$planId" params={{ productId: ctx.productId, planId: entry.link.id }}>{L.open}</Link></>
    : entry.link?.kind === "url" && entry.link.url ? <> · <a className="z-link" href={entry.link.url} target="_blank" rel="noreferrer noopener">{L.open} <ArrowSquareOutIcon size={11} /></a></> : null;
  // A teammate's step shows a plain person icon; only the reader's own steps are "you".
  const teammate = entry.actor === "person" && !entry.mine;
  return {
    actor: entry.actor === "zooi" || teammate ? undefined : entry.actor,
    engine: typeof entry.params.name === "string" ? entry.params.name : undefined,
    icon: entry.actor === "zooi" ? ZOOI_ICON[entry.code] ?? <InfoIcon size={12} weight="bold" /> : teammate ? <UserIcon size={12} weight="bold" /> : undefined,
    text: <>{text}{link}</>,
    at: dateTime(entry.at, ctx.zone),
  };
}
